import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import PasswordInput from '../../components/common/PasswordInput';
import './BridgeClubLogin.css';

const BridgeClubResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await bridgeClubAPI.resetPassword(token, password);
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/bridge-club/login');
      }, 2000);
    } catch (err: any) {
      setError(err.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="bridge-club-login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/rbf-logo.png" alt="Red Bridge Farm" className="login-logo" />
            <h1 className="login-title">Invalid Link</h1>
          </div>
          <div className="login-error">
            This password reset link is invalid. Please request a new one.
          </div>
          <Link to="/bridge-club/login" className="login-button" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bridge-club-login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="verification-icon">&#10003;</div>
            <h1 className="login-title">Password Reset!</h1>
          </div>
          <div className="verification-success">
            <p>Your password has been reset successfully.</p>
            <p className="check-spam">Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bridge-club-login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/rbf-logo.png" alt="Red Bridge Farm" className="login-logo" />
          <h1 className="login-title">Reset Password</h1>
          <p className="login-tagline">Create a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              minLength={8}
              required
              autoFocus
            />
            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
              Must be at least 8 characters
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
            />
          </div>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="login-footer">
          <p>Remember your password?</p>
          <Link to="/bridge-club/login" className="signup-link">Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubResetPassword;
